import type { ILot } from "@/models/lot";
import { StateOfSampling } from "@/models/production";

/**
 * Une concentration obtenue couvre-t-elle une concentration exigée ? Elle doit être supérieure ou
 * égale (décision produit 2026-07-27), comparée au centième.
 *
 * Mantis 0028534 : une commande exige des spz par dose, sa concentration vaut donc
 * spz / volume, non arrondie (2500 / 85 = 29.4118). Les concentrations finales proposées à la
 * production sont, elles, ces mêmes rapports arrondis au centième (29.41). Comparées brutes,
 * 29.41 < 29.4118 : une production réglée sur la concentration de la commande était jugée trop
 * peu concentrée pour elle. Au dispatch la saisie de la ligne était bloquée, aux compteurs la
 * dose ne rejoignait pas la colonne de la commande.
 */
export function roundConcentration(value: number): number {
    return Math.round(value * 100) / 100;
}

export function concentrationCovers(obtained: number, required: number): boolean {
    return roundConcentration(obtained) >= roundConcentration(required);
}

/** Le volume d'une part de lot hétéro : doses théoriques plus volume d'amorce. */
export interface PartOfLotVolume {
    volume: number;
    numberDosesTheoretical: number;
    primerVolume: number;
}

/**
 * Concentration de l'éjaculat en fonction de son statut (pur, pré-dilué, dilué…), en 10⁶ spz/ml,
 * arrondie au centième : la colonne « Conc en ML/ml » du suivi qualité et le bandeau de l'animal
 * (Mantis 0026617). `partOfLot` : les parts d'un lot hétéro, dont le volume fait celui du lot.
 */
export function concentrationWithStatus(lotConcentration: ILot, partOfLot: PartOfLotVolume[] = []): number {
    // Lot reçu d'un autre centre : la concentration FINALE de la dose est déjà portée par le
    // lot (BuildExternalLot la pose à SpzDose / VolumeDose). La production rattachée, elle, est
    // celle du centre émetteur : la recalculer ci-dessous à partir de son sample et de ses
    // bains rendrait la concentration du prélèvement d'ORIGINE, pas celle de la dose reçue.
    if (lotConcentration.isExternal) {
        return Math.floor((lotConcentration.concentration ?? 0) * 100) / 100;
    }

    let result: number = 0;

    // Sans sample (et hors hétéro), volume et concentration restent à 0.
    let volume = 0;
    let concentration = 0;
    if (lotConcentration.isHeterospermie) {
        volume =
            partOfLot?.reduce((acc, lot) => acc + lot.volume * lot.numberDosesTheoretical + lot.primerVolume, 0) ??
            0;
        concentration = lotConcentration.concentration;
    } else if (lotConcentration.production?.sample != undefined) {
        const sample = lotConcentration.production.sample;
        volume = sample.secondaryVolume > 0 ? sample.secondaryVolume : sample.volume;
        // Une seconde lecture d'absorbance remplace la concentration du dosage initial.
        concentration = sample.secondaryAbsorbance > 0 ? sample.secondaryConcentration : sample.concentration;
    }

    let nbrSpz = Math.floor(((volume * concentration) / 1000) * 100) / 100;

    let volumeUsed =
        lotConcentration.production?.dilutorBaths.reduce((acc, bath) => acc + (bath.volume ?? 0), 0) ?? 0;

    let volumeTotalStatut =
        lotConcentration.production?.samplingState.toString() == StateOfSampling[StateOfSampling.PreDilutedOneByOne]
            ? // si il est en 1:1, il existe une valeur dans les volume délivrés, cela doublonne le volume donc on le retire
            volume
            : volumeUsed + volume;

    switch (lotConcentration.production?.samplingState.toString()) {
        case StateOfSampling[StateOfSampling.Diluted]:
            result = (volume * concentration) / volumeTotalStatut;
            break;
        case StateOfSampling[StateOfSampling.Clean]:
            result = lotConcentration.production?.sample.concentration ?? 0;
            break;
        case StateOfSampling[StateOfSampling.PreDiluted]:
            result = (volume * concentration) / volumeTotalStatut;
            break;
        // Cas spécifique pour pré-dilution 1:1
        case StateOfSampling[StateOfSampling.PreDilutedOneByOne]:
            result = lotConcentration.production?.sample.secondaryConcentration ?? 0;
            break;
        default:
            if (lotConcentration.isHeterospermie) {
                result = lotConcentration.concentration;
            } else if (volume > 0) {
                result = (nbrSpz / volume) * 1000;
            } else {
                result = nbrSpz * 1000;
            }
    }

    return Math.floor(result * 100) / 100;
}
